import {
  ArrowDownload16Regular,
  Copy16Regular,
  Delete16Regular,
  Dismiss16Regular,
  Edit12Regular,
  Filter20Regular,
  FolderArrowRight16Regular,
  FolderOpen16Regular,
  Link16Regular,
  MoreHorizontal16Regular,
  Open16Regular,
  PanelRight16Regular,
  Share16Regular,
  Star16Regular,
  Video24Regular,
} from '@fluentui/react-icons';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { Button } from '@web/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@web/shared/ui/card';
import {
  DataTableCheckbox,
  DataTableCommand,
  DataTableCommandBar,
  DataTableSelectionCount,
} from '@web/shared/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@web/shared/ui/dropdown-menu';
import { FilterInput } from '@web/shared/ui/filter-input';
import { Link } from '@web/shared/ui/link';
import { Page } from '@web/shared/ui/page';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@web/shared/ui/table';
import { Toggle } from '@web/shared/ui/toggle';
import { cn } from '@web/shared/util/cn';
import { APP, AppGlyph, FileIcon, type App } from '../-theme/file-icons';

/**
 * A página inicial da vitrine: os cards de destaque, a lista "Recente" feita à mão com
 * `Table` — o DataTable completo está em `/theme/list` — e, embaixo, a referência de tokens.
 */
export const Route = createFileRoute('/theme/')({
  component: ThemePage,
});

/* ------------------------------------------------------------------ dados fictícios */

const RECENT_FILES: {
  name: string;
  href: string;
  place: string;
  opened: string;
  owner: string;
  app: App;
}[] = [
  {
    name: 'Proposta comercial',
    href: 'proposta-comercial',
    place: 'Meus Arquivos',
    opened: '27 de mar.',
    owner: 'Ana Souza',
    app: 'word',
  },
  {
    name: 'Relatório de vendas do mês',
    href: 'relatorio-de-vendas',
    place: 'Meus Arquivos',
    opened: '26 de mar.',
    owner: 'Ana Souza',
    app: 'excel',
  },
];

/* ------------------------------------------------------------------ peças da tela */

/** Menu "…" — 240px, raio 8, sombra 8, itens de 36px com ícone de 16. */
function ItemActionsMenu({
  trigger,
  onFavorite = false,
}: {
  trigger: ReactNode;
  onFavorite?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[240px]">
        <DropdownMenuItem>
          <Open16Regular />
          Abrir
        </DropdownMenuItem>
        <DropdownMenuItem>
          <FolderOpen16Regular />
          Abrir local
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Share16Regular />
          Compartilhar
        </DropdownMenuItem>
        <DropdownMenuItem>
          <Link16Regular />
          Copiar link
        </DropdownMenuItem>
        {onFavorite && (
          <DropdownMenuItem>
            <Star16Regular />
            Adicionar aos favoritos
          </DropdownMenuItem>
        )}
        <DropdownMenuItem>
          <Dismiss16Regular />
          Remover de Para você
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * O card de "Para você": 478 de largura máxima, 16 de padding, cabeçalho com divisória
 * (#E0E0E0), corpo com a coluna de texto e a miniatura de 114px. Em hover ganha a sombra 4 e
 * revela o botão "…".
 */
function HighlightCard({
  app,
  title,
  thumbnail,
  children,
}: {
  app: App;
  title: string;
  thumbnail: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="group border-stroke-surface bg-surface hover:shadow-fluent-4 focus-within:shadow-fluent-4 flex-1 gap-0 rounded-[12px] border p-4 shadow-none transition-shadow duration-(--duration-normal) ease-(--ease-fluent) lg:max-w-[478px]">
      <CardHeader className="border-border flex flex-row items-center justify-between gap-0 border-b p-0 pb-4!">
        <div className="flex min-w-0 items-center gap-[10px]">
          <AppGlyph app={app} />
          <h3 className="type-body1-strong truncate">{title}</h3>
        </div>
        <ItemActionsMenu
          trigger={
            <Button
              variant="ghost"
              aria-label="Mostrar mais ações para este item"
              className="text-fg-tertiary bg-surface h-5 w-7 rounded-[4px] p-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal16Regular className="size-4" />
            </Button>
          }
        />
      </CardHeader>

      <CardContent className="flex gap-3 p-0 pt-[19px]">
        <div className="flex min-w-0 flex-1 flex-col">
          {children}
          <CardFooter className="mt-auto p-0">
            <Button variant="outline" className="type-body1-strong h-[31px] rounded-[6px] px-3">
              Abrir
            </Button>
          </CardFooter>
        </div>
        <div className="bg-surface-subtle border-border flex size-[114px] shrink-0 items-center justify-center overflow-hidden rounded-[6px] border">
          {thumbnail}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Com itens selecionados, a barra de comandos do DataTable toma o lugar dos chips. Os comandos são os da página inicial; a lista completa tem os dela.
 */
function SelectionCommandBar({ count, onClear }: { count: number; onClear: () => void }) {
  return (
    <DataTableCommandBar
      end={
        <>
          <DataTableSelectionCount onClick={onClear}>{count} selecionado</DataTableSelectionCount>
          <DataTableCommand variant="outline" className="font-normal">
            <PanelRight16Regular />
            Detalhes
          </DataTableCommand>
        </>
      }
    >
      <DataTableCommand>
        <Delete16Regular />
        Excluir
      </DataTableCommand>
      <DataTableCommand>
        <Star16Regular />
        Adicionar aos favoritos
      </DataTableCommand>
      <DataTableCommand>
        <ArrowDownload16Regular />
        Baixar
      </DataTableCommand>
      <DataTableCommand>
        <Copy16Regular />
        Duplicata
      </DataTableCommand>
      <DataTableCommand>
        <FolderArrowRight16Regular />
        Mover para
      </DataTableCommand>
      <DataTableCommand iconOnly aria-label="Mais comandos">
        <MoreHorizontal16Regular />
      </DataTableCommand>
    </DataTableCommandBar>
  );
}

/** Círculo de 24px com tinta da marca — o "quem fez" do card. */
function ActivityDot({ children }: { children: ReactNode }) {
  return (
    <span className="bg-brand-tint text-brand-fg flex size-6 shrink-0 items-center justify-center rounded-full text-100 font-semibold">
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ a página */

function ThemePage() {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const allSelected = selected.size === RECENT_FILES.length;
  const headerState: boolean | 'indeterminate' = allSelected
    ? true
    : selected.size > 0
      ? 'indeterminate'
      : false;
  const toggleRow = (name: string, checked: boolean) =>
    setSelected((previous) => {
      const next = new Set(previous);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });
  const toggleAll = (checked: boolean) =>
    setSelected(checked ? new Set(RECENT_FILES.map((file) => file.name)) : new Set());

  return (
    <Page contentClassName="pr-[26px] pl-4 pb-16">
      <h1 className="type-title3 mt-[27px] ml-2">Para você</h1>

      <div className="mt-[23px] flex gap-4">
        <HighlightCard
          app="whiteboard"
          title="Quadro"
          thumbnail={
            <span className="bg-surface flex size-9 items-center justify-center rounded-[4px] shadow-fluent-2">
              <AppGlyph app="whiteboard" size={20} />
            </span>
          }
        >
          <div className="flex gap-2">
            <ActivityDot>
              <Edit12Regular className="size-3" />
            </ActivityDot>
            <div className="min-w-0">
              <p className="type-body1 truncate">Você editou</p>
              <p className="type-caption1 text-fg-tertiary">seg.</p>
            </div>
          </div>
        </HighlightCard>

        <HighlightCard
          app="loop"
          title="Reunião de pipeline"
          thumbnail={
            <span className="bg-surface-muted flex size-full items-center justify-center">
              <Video24Regular className="text-fg-tertiary size-8" />
            </span>
          }
        >
          <div className="flex gap-2">
            <ActivityDot>NC</ActivityDot>
            <div className="min-w-0">
              <p className="type-body1 truncate">
                <span className="font-semibold">Carlos Lima</span> gravou uma reunião
              </p>
              <p className="type-caption1 text-fg-tertiary">27 de ago.</p>
            </div>
          </div>
        </HighlightCard>
      </div>

      <h2 className="type-subtitle2 mt-[31px] ml-2">Recente</h2>

      {/* Com seleção, a barra de comandos toma o lugar dos chips de filtro (42x36 em pílula, 4px de vão). */}
      {selected.size > 0 ? (
        <div className="mt-4">
          <SelectionCommandBar count={selected.size} onClear={() => setSelected(new Set())} />
        </div>
      ) : (
        <div className="mt-4 flex h-9 items-center gap-1">
          <Button
            variant="outline"
            aria-label="Mais filtros"
            className="border-stroke-card h-9 w-[42px] rounded-full bg-transparent p-0"
          >
            <Filter20Regular className="size-5" />
          </Button>
          {(['word', 'excel', 'powerpoint', 'pdf'] as App[]).map((app) => (
            <Toggle
              key={app}
              aria-label={`Filtrar por ${APP[app].label}`}
              className="bg-surface-muted hover:bg-accent-selected data-[state=on]:bg-brand-tint h-9 w-[42px] rounded-full border border-transparent p-0"
            >
              <AppGlyph app={app} size={20} />
            </Toggle>
          ))}
          <FilterInput
            placeholder="Filtrar por nome ou pessoa"
            aria-label="Filtrar por nome ou pessoa"
            className="ml-auto w-[240px]"
          />
        </div>
      )}

      {/*
       * A lista: moldura de card com raio 12 e 12px de respiro embaixo. A tabela fica 8px
       * para dentro da moldura — é o recuo da faixa de hover, com os
       * cantos de raio 8 nas duas pontas.
       */}
      <div className="group/table border-stroke-surface bg-surface mt-3 rounded-[12px] border px-2 pb-3">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[45px]" />
            <col className="w-[500px]" />
            <col className="w-[200px]" />
            <col className="w-[260px]" />
            <col />
          </colgroup>
          <TableHeader className="group/head [&_tr]:border-stroke-subtle">
            <TableRow className="h-12 hover:bg-transparent">
              <TableHead className="h-12 p-0 text-center">
                <DataTableCheckbox
                  aria-label="Selecionar todas as linhas"
                  reveal="group-hover/head:opacity-100"
                  checked={headerState}
                  onToggle={toggleAll}
                />
              </TableHead>
              {['Nome', 'Aberto', 'Proprietário', 'Atividade'].map((column) => (
                <TableHead key={column} className="type-caption1-strong text-fg-tertiary h-12 px-0">
                  {column}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {RECENT_FILES.map((file) => (
              <TableRow
                key={file.name}
                data-state={selected.has(file.name) ? 'selected' : undefined}
                className={cn(
                  'group/row border-stroke-subtle h-14 last:border-b-0 [&>td:first-child]:rounded-l-lg [&>td:last-child]:rounded-r-lg',
                  'hover:bg-accent has-data-[state=open]:bg-accent data-[state=selected]:bg-selected data-[state=selected]:hover:bg-selected-hover data-[state=selected]:has-data-[state=open]:bg-selected-hover',
                )}
              >
                <TableCell className="p-0 text-center">
                  <DataTableCheckbox
                    aria-label="Selecionar linha"
                    reveal="group-hover/row:opacity-100 group-has-data-[state=open]/row:opacity-100"
                    checked={selected.has(file.name)}
                    onToggle={(checked) => toggleRow(file.name, checked)}
                  />
                </TableCell>

                <TableCell className="py-0 pr-0 pl-2">
                  <span className="flex items-center gap-4">
                    <FileIcon app={file.app} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <Link href={`#${file.href}`} variant="subtle" className="type-body1 truncate">
                        {file.name}
                      </Link>
                      <span className="type-caption1 text-fg-quaternary truncate">{file.place}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1 pr-2 opacity-0 group-hover/row:opacity-100 group-has-data-[state=open]/row:opacity-100 group-data-[state=selected]/row:opacity-100 focus-within:opacity-100">
                      <ItemActionsMenu
                        onFavorite
                        trigger={
                          <Button
                            variant="ghost"
                            aria-label="Mostrar mais ações para este item"
                            className="text-fg-tertiary size-7 rounded-[4px] p-0 data-[state=open]:opacity-100"
                          >
                            <MoreHorizontal16Regular className="size-4" />
                          </Button>
                        }
                      />
                      <Button
                        variant="ghost"
                        aria-label="Compartilhar este item"
                        className="text-fg-tertiary size-7 rounded-[4px] p-0"
                      >
                        <Share16Regular className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        aria-label="Adicionar aos favoritos"
                        className="text-fg-tertiary size-7 rounded-[4px] p-0"
                      >
                        <Star16Regular className="size-4" />
                      </Button>
                    </span>
                  </span>
                </TableCell>

                <TableCell className="type-body1 text-fg-tertiary px-2 py-0">
                  {file.opened}
                </TableCell>
                <TableCell className="type-body1 text-fg-tertiary truncate px-2 py-0">
                  {file.owner}
                </TableCell>
                <TableCell className="px-2 py-0">
                  <span className="sr-only">Sem atividade</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <TokenReference />
    </Page>
  );
}

/* ------------------------------------------------------------------ referência */

function Swatch({ token, value }: { token: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="border-stroke-card size-8 shrink-0 rounded-md border"
        style={{ background: `var(${value})` }}
      />
      <div className="min-w-0">
        <p className="type-caption1 truncate font-mono">{token}</p>
        <p className="type-caption1 text-fg-tertiary truncate font-mono">{value}</p>
      </div>
    </div>
  );
}

const TYPE_ROLES = [
  ['type-caption1', '12/16 · 400', 'Legenda — metadado'],
  ['type-caption1-strong', '12/16 · 600', 'Rótulo de coluna da lista'],
  ['type-body1', '14/20 · 400', 'Corpo — célula, item de menu, texto corrido'],
  ['type-body1-strong', '14/20 · 600', 'Rótulo de grupo, botão, comando'],
  ['type-body2', '16/22 · 400', 'Mensagem assistiva'],
  ['type-subtitle2', '16/22 · 600', 'Título de seção — "Recente"'],
  ['type-subtitle1', '20/28 · 600', 'Subtítulo'],
  ['type-title3', '24/32 · 600', 'Título de página — "Para você"'],
  ['type-title2', '28/36 · 600', 'Título grande'],
  ['type-title1', '32/40 · 600', 'Título de destaque'],
  ['type-large-title', '40/52 · 600', 'Título de capa'],
  ['type-display', '68/92 · 600', 'Display'],
] as const;

function TokenReference() {
  const groups = [
    {
      title: 'Superfície',
      swatches: [
        ['background (página)', '--background'],
        ['surface (card)', '--card'],
        ['surface-subtle', '--neutral-bg-2'],
        ['surface-muted', '--neutral-bg-3'],
        ['accent (hover)', '--accent'],
        ['accent-selected', '--accent-selected'],
        ['accent-pressed', '--accent-pressed'],
        ['sidebar-accent (nav hover)', '--sidebar-accent'],
        ['sidebar-accent-pressed', '--sidebar-accent-pressed'],
      ],
    },
    {
      title: 'Texto',
      swatches: [
        ['foreground', '--foreground'],
        ['fg-secondary', '--neutral-fg-2'],
        ['fg-tertiary', '--neutral-fg-3'],
        ['muted-foreground', '--muted-foreground'],
      ],
    },
    {
      title: 'Traço',
      swatches: [
        ['input', '--input'],
        ['input-hover', '--input-hover'],
        ['border', '--border'],
        ['stroke-card', '--neutral-stroke-card'],
        ['stroke-subtle', '--neutral-stroke-3'],
      ],
    },
    {
      title: 'Marca e estado',
      swatches: [
        ['primary', '--primary'],
        ['primary-hover', '--primary-hover'],
        ['primary-pressed', '--primary-pressed'],
        ['brand-tint', '--brand-tint'],
        ['success', '--success'],
        ['warning', '--warning'],
        ['destructive', '--destructive'],
      ],
    },
  ] as const;

  const metrics = [
    ['Barra superior', '48px'],
    ['Navegação', '280px'],
    ['Item de navegação', '36px'],
    ['Subitem de navegação', '32px'],
    ['Campo', '32px'],
    ['Botão', '36px'],
    ['Linha de lista (1 linha)', '48px'],
    ['Linha de lista (2 linhas)', '56px'],
    ['Raio do card', '12px'],
    ['Raio do controle', 'pílula'],
    ['Menu de contexto', '240px · item 36px'],
    ['Corpo do texto', '14 / 20'],
    ['Título de página', '24 / 32'],
    ['Título de seção', '16 / 22'],
    ['Rótulo de coluna', '12 semibold'],
  ] as const;

  return (
    <section className="pt-12">
      <h2 className="type-subtitle2 pb-1">Tokens do tema</h2>
      <p className="type-body1 text-fg-tertiary pb-4">
        A rampa clara é a referência; a escura é derivada dela.
      </p>

      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {groups.map((group) => (
          <div
            key={group.title}
            className="border-stroke-surface bg-surface rounded-[12px] border p-4"
          >
            <p className="type-body1-strong pb-3">{group.title}</p>
            <div className="flex flex-col gap-2">
              {group.swatches.map(([token, value]) => (
                <Swatch key={token} token={token} value={value} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="border-stroke-surface bg-surface mt-6 rounded-[12px] border p-4">
        <p className="type-body1-strong pb-1">Tipografia</p>
        <p className="type-caption1 text-fg-tertiary pb-4">Tela usa papel, nunca tamanho cru.</p>
        <div className="grid gap-x-8 gap-y-3 lg:grid-cols-2">
          {TYPE_ROLES.map(([role, spec, usage]) => (
            <div
              key={role}
              className="flex items-baseline justify-between gap-4 border-b border-stroke-subtle pb-2"
            >
              <span className={cn(role, 'truncate')}>{usage}</span>
              <span className="type-caption1 text-fg-tertiary shrink-0 text-right font-mono">
                {role.replace('type-', '')} · {spec}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-stroke-surface bg-surface mt-6 rounded-[12px] border p-4">
        <p className="type-body1-strong pb-3">Densidade</p>
        <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
          {metrics.map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between gap-2">
              <dt className="type-body1 text-fg-tertiary">{label}</dt>
              <dd className="type-body1-strong tabular">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
